<script lang="ts" setup>
import SkrButton from '@/components/button/SkrButton.vue'
import DialogFreePlanDowngrade from '@/components/dialogs/business/DialogFreePlanDowngrade.vue'
import Logo from '@/components/Logo.vue'

const userStore = useUserStore()
const businessStore = useBusinessStore()
const widgetStore = useWidgetStore()
const paymentStore = usePaymentStore()

const { isChargebeeTrial } = storeToRefs(businessStore)

const { t } = useI18n()

const isSwitching = ref(false)
const { startPolling, isPolling } = usePaymentStatusPolling()

const { openPortal } = useChargebee({
  onClose: async () => {
    await businessStore.getBusiness()
  },
  onPaymentSourceAdd: () => {
    startPolling()
  },
  onPaymentSourceUpdate: () => {
    startPolling()
  },
})

const isDowngradeDialogShown = ref(false)

const action = async () => {
  if (businessStore.isPaymentUpdateRequired) {
    await openChargebeePortal()
  } else {
    // legacy, customers which were not migrated
    await upgrade()
  }
}

const upgrade = async () => {
  await navigateTo({ name: 'business-upgrade' })
}

const openChargebeePortal = async () => {
  await openPortal(businessStore.id)
}

const switchToStarterOrFree = async () => {
  isSwitching.value = true
  if (isChargebeeTrial.value) {
    const subscriptionPayload: UpgradeSubscriptionPayload = {
      type: 'STARTER',
      cycle: 'daily',
      seats: 1,
    }

    await paymentStore.upgradeSubscription(subscriptionPayload)
    await userStore.fetchUser()
    await navigateTo({ name: 'index' })
  } else {
    await businessStore.deleteBusiness()
    await userStore.fetchUser()
    await navigateTo({ name: 'index' })
    businessStore.clearBusiness()
    await businessStore.getBusiness()
    widgetStore.setDialog('business/DialogTransactionalCsat', {})
  }
  isSwitching.value = false
}

const title = computed(() => {
  return businessStore.isTrialExpired
    ? t('business.navigation.locked.title')
    : t('business.navigation.locked_failed_payments.title')
})

const text = computed(() => {
  if (businessStore.isPaymentUpdateRequired) {
    return t('business.navigation.locked_payment_update_required.text')
  } else if (businessStore.isTrialExpired) {
    return t('business.navigation.locked.text')
  } else {
    return t('business.navigation.locked_failed_payments.text')
  }
})

const actionLabel = computed(() => {
  if (businessStore.isPaymentUpdateRequired) {
    return t('business.billing.plan.manage')
  } else {
    return t('business.navigation.locked.choose_plan')
  }
})

const downgradeLabel = computed(() => {
  return isChargebeeTrial.value
    ? t('business.navigation.locked.change_to_starter')
    : t('business.navigation.locked.change_to_free')
})
</script>

<template>
  <div>
    <div class="nav-overlay">
      <div class="nav-overlay__logo">
        <logo admin-logo-variant="light" />
      </div>
      <div class="nav-overlay__fade" />
      <div class="nav-overlay__content">
        <div class="text-h5 text-white font-weight-bold" data-cy="locked_admin_nav_title">{{ title }}</div>
        <p class="text-grey-light font-weight-regular">{{ text }}</p>
        <skr-button block size="lg" :loading="isPolling" @click="action">{{ actionLabel }}</skr-button>
        <skr-button
          type="text"
          color="grey-dark"
          block
          size="lg"
          data-cy="downgrade_button"
          @click="isDowngradeDialogShown = true"
        >
          {{ downgradeLabel }}
        </skr-button>
      </div>
      <div class="nav-overlay__logout">
        <v-divider />
        <v-list>
          <v-list-item prepend-icon="custom:leave" :to="{ name: 'logout' }">
            <v-list-item-title>{{ t('global.navigation.logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </div>
    </div>
    <dialog-free-plan-downgrade
      v-model="isDowngradeDialogShown"
      :loading="isSwitching"
      @accept="switchToStarterOrFree"
    />
  </div>
</template>

<style lang="scss" scoped>
$content-padding: 5 * $bw;

.nav-overlay {
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  backdrop-filter: blur(8px);
  pointer-events: all;

  &__logo {
    padding: 9 * $bw $content-padding;
    flex: 1;
  }

  &__fade {
    width: 100%;
    height: 10vh;
    background-image: linear-gradient(to top, $c-mininav-bg, transparent);
  }

  &__content {
    background-color: $c-mininav-bg;
    padding: $content-padding 8 * $bw;

    > * + * {
      margin-top: 4 * $bw;
    }

    p {
      font-size: 0.875rem;
    }
  }

  &__logout {
    height: 20 * $bw;
    background-color: $c-mininav-bg;
    flex-grow: 0;

    .v-list-item {
      padding: 20px 0 20px 20px;
      min-height: 0;

      @include maw {
        padding: 12px 0 12px 20px;
      }

      &.business-nav-bottom-flyout {
        padding: 20px;

        .v-icon__component {
          @include mini-nav-item-hover;
        }

        &:hover {
          .v-icon__component {
            color: white !important;
            caret-color: white !important;
          }
        }
      }

      &__action {
        margin: 0;
      }

      &__content {
        padding: 0;
      }

      &__avatar {
        margin: 0 12px 0 0;
      }

      &.business-profile {
        padding: 24px 20px 24px 12px;

        @include maw {
          padding: 16px 20px 16px 12px;
        }

        .v-icon__component {
          @include mini-nav-item-hover;
        }

        &:hover {
          .v-icon__component {
            color: white !important;
            caret-color: white !important;
          }
        }
      }
    }
  }
}
</style>
